// ============================================================================
// ThematicSaveControl
// ----------------------------------------------------------------------------
// Compact industrial Save icon control for the QuoteMaker top navbar.
//
// Replaces the wide (~85px) text pill with a precision 32px x 30px icon button
// styled to match .btn-nav-new.btn-nav-icon (same height and width as the [←]
// back button).
//
// Supports 3 dynamic states:
//   1. SAVED: Crisp emerald diskette with central checkmark badge and green LED.
//      Clickable to provide instant re-save reassurance.
//   2. SAVING: Warm amber diskette with animated orbital sync ring and pulsing LED.
//      Aria-busy and disabled while write is in flight.
//   3. DIRTY / PRESS TO SAVE: Brand orange diskette with downward save arrow
//      inviting immediate manual save (mirrors Ctrl+S).
//
// Accessibility & characterization:
//   Contains <span className="sr-only">{state}</span> so screen readers and
//   nav-actions characterization tests receive 'SAVED', 'SAVING', or 'SAVE'.
// ============================================================================

import React, { useState } from 'react';

export interface ThematicSaveControlProps {
    /** True if an auto-save or manual save operation is currently in flight */
    isSaving: boolean;
    /** True if there are unsaved changes waiting to be persisted */
    hasUnsavedChanges?: boolean;
    /** Timestamp of last successful save */
    lastSavedAt?: Date | null;
    /** When true, the control is disabled (e.g. quote generation in flight) */
    disabled?: boolean;
    /** Tooltip reason when disabled */
    disabledTitle?: string;
    /** Callback triggered when user clicks to save */
    onSave?: () => void;
    /** Visual variant: 'navbar' (default) or 'stage5' */
    variant?: 'navbar' | 'stage5';
    /** Optional extra class names */
    className?: string;
}

export function ThematicSaveControl({
    isSaving,
    hasUnsavedChanges = false,
    lastSavedAt = null,
    disabled = false,
    disabledTitle,
    onSave,
    variant = 'navbar',
    className = '',
}: ThematicSaveControlProps) {
    const [justSavedFlash, setJustSavedFlash] = useState(false);

    // Determine state
    const state: 'saving' | 'dirty' | 'saved' = isSaving
        ? 'saving'
        : hasUnsavedChanges
        ? 'dirty'
        : 'saved';

    // Tooltip text
    const getTooltip = () => {
        if (disabled && disabledTitle) return disabledTitle;
        if (state === 'saving') return 'Saving changes to disk and database…';
        if (state === 'dirty') return 'Unsaved changes · Click to save (Ctrl+S)';
        if (justSavedFlash) return 'Saved just now!';
        if (lastSavedAt instanceof Date && !isNaN(lastSavedAt.getTime())) {
            const timeStr = lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            return `All changes saved at ${timeStr} · Click to re-save (Ctrl+S)`;
        }
        return 'All changes saved automatically · Click to re-save (Ctrl+S)';
    };

    const handleClick = () => {
        if (disabled || isSaving) return;
        if (onSave) {
            onSave();
        }
        setJustSavedFlash(true);
        setTimeout(() => setJustSavedFlash(false), 1200);
    };

    // Style configuration based on state
    let accentClass = 'accent-emerald';
    let stateLabel = 'SAVED';

    if (state === 'saving') {
        accentClass = 'accent-amber';
        stateLabel = 'SAVING';
    } else if (state === 'dirty') {
        accentClass = 'accent-orange';
        stateLabel = 'SAVE';
    }

    const stage5Class = variant === 'stage5' ? 'qm-nav-stage5-action qm-nav-stage5-action--save' : '';

    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={disabled || state === 'saving'}
            className={`btn-nav-new btn-nav-icon ${accentClass} ${stage5Class} ${className} relative overflow-hidden transition-all duration-200`}
            style={{
                width: '32px',
                height: '30px',
                padding: 0,
                cursor: disabled ? 'not-allowed' : state === 'saving' ? 'wait' : 'pointer',
                opacity: disabled ? 0.4 : 1,
            }}
            aria-label={state === 'dirty' ? 'Save quote' : state === 'saving' ? 'Saving quote' : 'All Stage 5 quote changes are saved'}
            aria-live="polite"
            aria-busy={state === 'saving'}
            title={getTooltip()}
        >
            {/* Screen reader text for accessibility and test suites */}
            <span className="sr-only">{stateLabel}</span>

            {/* Thematic Precision 3.5" Diskette SVG */}
            <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="transition-transform duration-150 active:scale-90"
                aria-hidden="true"
            >
                {/* Diskette Outer Perimeter with signature chamfered top-right corner */}
                <path
                    d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />

                {/* Top Slider Shutter (Drive Slot) */}
                <path
                    d="M7 3v4h7V3"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    opacity={state === 'saving' ? '0.5' : '0.75'}
                />

                {/* Center / Lower Label & Data Inset */}
                <rect
                    x="7"
                    y="11"
                    width="10"
                    height="9"
                    rx="1"
                    stroke="currentColor"
                    strokeWidth="1.25"
                    opacity="0.6"
                    fill={state === 'dirty' ? 'var(--qm-accent-a15)' : 'rgba(0, 0, 0, 0.25)'}
                />

                {/* Status-specific badge in label area */}
                {state === 'saved' && (
                    <>
                        {/* Crisp checkmark */}
                        <path
                            d="M9 15.5l2 2 4-4"
                            stroke="#34d399"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        {/* Status LED Dot (Emerald) */}
                        <circle cx="5.5" cy="5.5" r="1.1" fill="#34d399" />
                    </>
                )}

                {state === 'dirty' && (
                    <>
                        {/* Downward save arrow indicating ready-to-write */}
                        <path
                            d="M12 13v5m-2-2l2 2 2-2"
                            stroke="var(--qm-accent)"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        {/* Status LED Dot (Orange alert) */}
                        <circle cx="5.5" cy="5.5" r="1.1" fill="var(--qm-accent)" className="animate-pulse" />
                    </>
                )}

                {state === 'saving' && (
                    <>
                        {/* Inner sync arc */}
                        <path
                            d="M10 15a2.5 2.5 0 0 1 4 0"
                            stroke="#fbbf24"
                            strokeWidth="2"
                            strokeLinecap="round"
                        />
                        {/* Status LED Dot (Amber active write) */}
                        <circle cx="5.5" cy="5.5" r="1.1" fill="#fbbf24" className="animate-ping" />
                    </>
                )}
            </svg>

            {/* Orbiting Circular Sync Spinner when Saving */}
            {state === 'saving' && (
                <span
                    className="absolute inset-[3px] rounded-full border-[1.5px] border-amber-400 border-t-transparent animate-spin pointer-events-none"
                    style={{ opacity: 0.9 }}
                    aria-hidden="true"
                />
            )}

            {/* Flash ripple on manual save click */}
            {justSavedFlash && (
                <span
                    className="absolute inset-0 rounded-md bg-emerald-400/30 animate-ping pointer-events-none"
                    aria-hidden="true"
                />
            )}
        </button>
    );
}
